import type { Match } from "navigo";
import type { ChildDom } from "vanjs-core";
import { reactive } from "vanjs-ext";
import { errorSection } from "../error-section";
import { loadingScreen } from "../loading-screen";
import type { Route, RouteContext } from "../router";
import { centeredBox } from "../van/centered-box";
import {
	button,
	div,
	h1,
	label,
	option,
	p,
	select,
	span,
} from "../van/van-tags";
import { sp2Placeholder } from "./sp2";

export class Sp2FotaPage implements Route {
	async #getSmartplug2(imei: string): Promise<void> {
		const req = this.#ctx.api.get(`/smartplug2s/${imei}`);
		req.setSignedInUserAuth();

		const res = await loadingScreen.waitFor(req.do());
		if (res.ok) {
			this.#reactive.sp2 = await res.json();
		}
	}

	async #listFirmwares(): Promise<void> {
		const req = this.#ctx.api.get("/firmwares/smartplug2");
		req.setSignedInUserAuth();

		const res = await loadingScreen.waitFor(req.do());
		if (!res.ok) {
			errorSection.setText(await res.text());
			return;
		}

		this.#reactive.firmwares = await res.json();

		if (this.#reactive.firmwares.length) {
			this.#data.selectedFile = this.#reactive.firmwares[0];
		}
	}

	async #putSp2(): Promise<void> {
		const req = this.#ctx.api.put(
			`/smartplug2s/${this.#reactive.sp2.Imei}`,
		);
		req.setJsonBody(this.#reactive.sp2);
		req.setSignedInUserAuth();

		errorSection.clear();
		const res = await loadingScreen.waitFor(req.do());
		if (res.ok) {
			this.#reactive.sp2 = await res.json();
		} else {
			errorSection.setText(await res.text());
		}
	}

	async #uploadFotaConfig(): Promise<void> {
		this.#reactive.sp2.Fota.Approved = !this.#data.requireUserApproval;
		this.#reactive.sp2.Fota.Filename = this.#data.selectedFile;
		await this.#putSp2();
	}

	async refresh(match: Match): Promise<void> {
		if (!match?.data?.imei) throw "missing path param: imei";
		const imei = match.data.imei;
		await this.#getSmartplug2(imei);
		await this.#listFirmwares();
	}

	#ctx: RouteContext;

	#data = {
		requireUserApproval: true,
		selectedFile: "",
	};

	#firmwareSelect: ChildDom = () =>
		div({ class: "select" }, () =>
			select(
				{
					onchange: (e: Event) => {
						if (!(e.target instanceof HTMLSelectElement)) return;
						this.#data.selectedFile = e.target.value;
					},
				},
				this.#reactive.firmwares.map((filename) =>
					option({ value: filename }, filename),
				),
			),
		);

	#reactive = reactive({
		firmwares: [""],
		sp2: sp2Placeholder,
	});

	#userApprovalSelect: ChildDom = () =>
		select(
			{
				onchange: (e: Event) => {
					if (!(e.target instanceof HTMLSelectElement)) return;
					this.#data.requireUserApproval = !!e.target.value;
				},
			},
			option(
				{
					value: "true",
				},
				"Yes",
			),
			option(
				{
					value: "",
				},
				"No",
			),
		);

	ChildDom: ChildDom;

	constructor(ctx: RouteContext) {
		this.#ctx = ctx;
		this.ChildDom = centeredBox(
			{ size: "narrow" },
			h1({ class: "title is-4" }, "SmartPlug2 FOTA"),
			p(
				{ class: "subtitle is-6" },
				() => `IMEI ${this.#reactive.sp2.Imei}`,
			),
			div(
				{ class: "field" },
				label({ class: "label" }, "Current Firmware"),
				span(
					{ class: "tag is-medium" },
					() => this.#reactive.sp2.Firmware,
				),
			),
			div(
				{ class: "field" },
				label({ class: "label" }, "Target Firmware"),
				div({ class: "control" }, this.#firmwareSelect),
			),
			div(
				{ class: "field" },
				label({ class: "label" }, "Require User Approval"),
				div(
					{ class: "control" },
					div({ class: "select" }, this.#userApprovalSelect),
				),
			),
			div(
				{ class: "buttons mt-5" },
				button(
					{
						class: "button is-primary",
						onclick: () => this.#uploadFotaConfig(),
					},
					"Upload",
				),
				button(
					{
						class: "button is-danger",
						onclick: () => this.#ctx.router.up(),
					},
					"Back",
				),
			),
		);
	}
}
